/**
 * A tabela de lançamentos de uma casa, com o vazio e o "+ Novo lançamento".
 *
 * Papel: reproduz o `tableFor` da origem — Data, Descrição, Categoria, Valor,
 * Status, Obs. e as ações "editar" e "remover" —, e, sem linha nenhuma, o bloco
 * "Nenhum lançamento registrado ainda.".
 *
 * Como usar: quem monta passa `onNovo`, `onEditar` e `onRemover` apenas para
 * quem tem `editar` em `residencias`. Sem `onEditar` e `onRemover` a coluna de
 * ações some inteira, cabeçalho incluído; sem `onNovo` não há "+ Novo
 * lançamento" nem no vazio nem acima da tabela. A confirmação de remover é de
 * quem monta.
 *
 * Com linhas, o "+ Novo lançamento" fica acima da tabela, numa fileira com 16px
 * até ela, o desenho que a origem usa para o mesmo botão nos assuntos da
 * fazenda. Sem essa fileira, a casa que já tem uma linha ficaria sem como
 * receber outro lançamento.
 *
 * O valor sai com o sinal depois do cifrão, como o `fmt` da origem
 * (`R$ -1.234,50`), e não pelo `formatarDinheiro`, que o `Intl` escreve
 * `-R$ 1.234,50`. Zero sai `R$ 0,00`, e não travessão, porque o valor vazio é
 * gravado como zero. As células ficam alinhadas à esquerda, como toda célula da
 * origem.
 */
import { type LancamentoResidencia } from '@concierge/shared';

import { formatarData, formatarNumero } from '@/shared/lib/formato';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Vazio } from '@/shared/ui/vazio';
import { ROTULOS_CATEGORIA, ROTULOS_STATUS, TOM_DO_STATUS } from '../rotulos';

interface TabelaDeLancamentosProps {
  /** Os lançamentos do bloco, na ordem de criação. */
  lancamentos: LancamentoResidencia[];
  /** Abre o formulário de lançamento novo; ausente esconde o "+ Novo lançamento". */
  onNovo?: () => void;
  /** Chamado com o lançamento da linha; ausente esconde a coluna de ações. */
  onEditar?: (lancamento: LancamentoResidencia) => void;
  /** Chamado com o lançamento a remover; a confirmação é de quem monta. */
  onRemover?: (lancamento: LancamentoResidencia) => void;
  /** Verdadeiro enquanto uma remoção está em andamento; desabilita "remover" em todas as linhas. */
  removendo?: boolean;
}

/**
 * Escreve o valor como o `fmt` da origem: `R$ `, o sinal, e o número com ponto
 * de milhar e vírgula decimal.
 *
 * @param valor - O valor do lançamento, em reais.
 * @returns Como `R$ -1.234,50`.
 */
function formatarValorDoLancamento(valor: number): string {
  return `R$ ${valor < 0 ? '-' : ''}${formatarNumero(Math.abs(valor))}`;
}

/**
 * A tabela de lançamentos de um bloco.
 *
 * @param lancamentos - Os lançamentos do bloco, na ordem de criação.
 * @param onNovo - Abre o formulário de lançamento novo; ausente esconde o botão.
 * @param onEditar - Chamado ao clicar em "editar"; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em "remover"; ausente esconde as ações.
 * @param removendo - Desabilita "remover" enquanto uma remoção não terminou.
 * @returns O vazio, quando não há linha, ou a tabela com o botão acima dela.
 */
export function TabelaDeLancamentos({
  lancamentos,
  onNovo,
  onEditar,
  onRemover,
  removendo = false,
}: TabelaDeLancamentosProps) {
  if (lancamentos.length === 0) {
    return (
      <Vazio
        mensagem="Nenhum lançamento registrado ainda."
        className="text-sm/[1.6]"
        rotuloAcao={onNovo === undefined ? undefined : '+ Novo lançamento'}
        onAcao={onNovo}
        varianteAcao="default"
      />
    );
  }

  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <>
      {onNovo !== undefined && (
        <div className="mb-4 flex gap-3">
          <Button className="text-sm/[normal]" onClick={onNovo}>
            + Novo lançamento
          </Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Obs.</TableHead>
            {mostrarAcoes && (
              <TableHead>
                <span className="sr-only">Ações</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lancamentos.map((lancamento) => (
            <TableRow key={lancamento.id}>
              <TableCell>{formatarData(lancamento.data)}</TableCell>
              <TableCell className="text-texto-medio">{lancamento.descricao}</TableCell>
              <TableCell>{ROTULOS_CATEGORIA[lancamento.categoria]}</TableCell>
              <TableCell>{formatarValorDoLancamento(lancamento.valor)}</TableCell>
              <TableCell>
                <Badge variant={TOM_DO_STATUS[lancamento.status]}>
                  {ROTULOS_STATUS[lancamento.status]}
                </Badge>
              </TableCell>
              <TableCell className="text-[12.5px] text-texto-fraco">
                {lancamento.observacao ?? '—'}
              </TableCell>
              {mostrarAcoes && (
                <TableCell>
                  <div className="flex gap-2">
                    {onEditar !== undefined && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs/[normal]"
                        onClick={() => onEditar(lancamento)}
                      >
                        editar
                      </Button>
                    )}
                    {onRemover !== undefined && (
                      <Button
                        variant="destructive"
                        className="text-xs/[normal]"
                        disabled={removendo}
                        onClick={() => onRemover(lancamento)}
                      >
                        remover
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
